import React, { useCallback, useMemo } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Image,
  Platform,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { connect } from "react-redux";
import { MaterialIcons as Icon } from "@expo/vector-icons";
import { BRANDING } from "@env";
import { setIsLogGroup, setLogGroupSelected } from "../../actions/loggroups";
import { setFormsFromAPI } from "../../actions/tasks";
import { simpleAlert } from "../../utils";

const HIDDEN_SCREENS = [
  "Login",
  "Forgot",
  "Register",
  "FormView",
  "GroupView",
  "SubmenuView",
];

const ROUTE_TAB_MAP = {
  MainView: "dashboard",
  RecordsView: "records",
  PromptFormView: "forms",
};

const TAB_ICONS = {
  dashboard: {
    selected: require("../../../assets/Icons-Screens/dashboard-selected.png"),
    unselected: require("../../../assets/Icons-Screens/dashboard-unselected.png"),
  },
  inventory: {
    selected: require("../../../assets/Icons-Screens/inventory-selected.png"),
    unselected: require("../../../assets/Icons-Screens/inventory-unselected.png"),
  },
  production: {
    selected: require("../../../assets/Icons-Screens/production-selected.png"),
    unselected: require("../../../assets/Icons-Screens/production-unselected.png"),
  },
  records: {
    selected: require("../../../assets/Icons-Screens/records-selected.png"),
    unselected: require("../../../assets/Icons-Screens/records-unselected.png"),
  },
};

function BottomNav({
  navigationRef,
  authToken,
  userRole,
  isOnline,
  currentRoute,
  formsFilter,
  setIsLogGroup,
  setLogGroupSelected,
  setFormsFromAPI,
}) {
  const insets = useSafeAreaInsets();
  const screen = currentRoute;

  const showInternetWarning = useCallback(() => {
    simpleAlert(
      "Warning",
      "This functionality requires internet, please reconnect and try again.",
      "OK"
    );
  }, []);

  const navigateTo = useCallback(
    (routeName, beforeNavigate) => {
      const nav = navigationRef?.current;
      if (!nav) return;

      if (!isOnline && (routeName === "NewTask" || routeName === "RecordsView")) {
        showInternetWarning();
        return;
      }
      beforeNavigate?.();
      if (screen !== routeName) {
        nav.navigate(routeName);
      }
    },
    [isOnline, navigationRef, screen, showInternetWarning]
  );

  const openFormsMenu = useCallback(
    (filter) => {
      navigateTo("NewTask", () => {
        setIsLogGroup(false);
        setFormsFromAPI(filter);
      });
    },
    [navigateTo, setFormsFromAPI, setIsLogGroup]
  );

  const tabs = useMemo(
    () =>
      [
        {
          key: "dashboard",
          label: "Dashboard",
          onPress: () => navigateTo("MainView"),
        },
        {
          key: "forms",
          label: "Forms",
          hidden: userRole === "Auditor",
          onPress: () => openFormsMenu(null),
        },
        {
          key: "inventory",
          label: "Inventory",
          onPress: () => openFormsMenu("inventoryForms"),
        },
        {
          key: "production",
          label: "Production",
          onPress: () => openFormsMenu("productionForms"),
        },
        {
          key: "records",
          label: "Records",
          onPress: () =>
            navigateTo("RecordsView", () => {
              setLogGroupSelected({});
              setIsLogGroup(false);
            }),
        },
      ].filter((tab) => !tab.hidden),
    [navigateTo, openFormsMenu, setIsLogGroup, setLogGroupSelected, userRole]
  );

  if (
    BRANDING !== "iqops" ||
    !authToken ||
    !screen ||
    HIDDEN_SCREENS.includes(screen)
  ) {
    return null;
  }

  const activeTab = (() => {
    if (screen === "NewTask" || screen === "NewLogTask") {
      if (formsFilter === "inventoryForms") return "inventory";
      if (formsFilter === "productionForms") return "production";
      return "forms";
    }
    return ROUTE_TAB_MAP[screen] || null;
  })();
  const bottomOffset = Math.max(insets.bottom, 8);

  return (
    <View
      pointerEvents="box-none"
      style={[styles.wrapper, { paddingBottom: bottomOffset }]}
    >
      <View style={styles.shadowWrap}>
        <View style={[styles.pill, styles.pillGradient]}>
          <View style={styles.row}>
            {tabs.map((tab) => {
              const isActive = activeTab === tab.key;
              return (
                <TouchableOpacity
                  key={tab.key}
                  style={styles.tab}
                  activeOpacity={0.75}
                  onPress={tab.onPress}
                >
                  <View
                    style={[
                      styles.iconWrap,
                      isActive && styles.iconWrapActive,
                    ]}
                  >
                    {tab.key === "forms" ? (
                      <Icon name="description" size={22} color="#fff" />
                    ) : (
                      <Image
                        source={
                          isActive
                            ? TAB_ICONS[tab.key].selected
                            : TAB_ICONS[tab.key].unselected
                        }
                        style={[styles.icon, { tintColor: "#fff" }]}
                        resizeMode="contain"
                      />
                    )}
                  </View>
                  <Text style={styles.label} numberOfLines={1}>
                    {tab.label}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </View>
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    position: Platform.OS === "web" ? "fixed" : "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 20,
    alignItems: "center",
    paddingHorizontal: 16,
  },
  shadowWrap: {
    width: "100%",
    maxWidth: 420,
    borderRadius: 36,
    shadowColor: "#0b4dad",
    shadowOpacity: 0.22,
    shadowRadius: 12,
    shadowOffset: { width: 0, height: 4 },
    elevation: 8,
  },
  pill: {
    borderRadius: 36,
    overflow: "hidden",
    minHeight: 72,
    justifyContent: "center",
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: "rgba(255,255,255,0.45)",
  },
  // #dde6f3 100% → #0b4dad 60% → #70abff 87%, bottom to top
  pillGradient: {
    backgroundColor: "transparent",
    backgroundImage:
      "linear-gradient(0deg, rgba(112,171,255,0.96) 0%, rgba(11,77,173,0.72) 50%, rgba(112,171,255,0.95) 100%)",
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 8,
    paddingVertical: 8,
  },
  tab: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  iconWrap: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 2,
  },
  iconWrapActive: {
    backgroundColor: "rgba(255,255,255,0.35)",
  },
  icon: {
    width: 24,
    height: 24,
  },
  label: {
    color: "#fff",
    fontSize: 10,
    fontWeight: "600",
    textAlign: "center",
  },
});

export default connect(
  (state) => ({
    authToken: state.user.get("authToken"),
    userRole: state.user.get("userRole"),
    isOnline: true,
    currentRoute: state.user.get("currentRoute"),
    formsFilter: state.tasks.get("formsFilter"),
  }),
  (dispatch) => ({
    setIsLogGroup: (value) => dispatch(setIsLogGroup(value)),
    setLogGroupSelected: (value) => dispatch(setLogGroupSelected(value)),
    setFormsFromAPI: (value) => dispatch(setFormsFromAPI(value)),
  })
)(BottomNav);
